import { useState, useEffect } from "react";
import "../App.css";

const API_URL = process.env.REACT_APP_API_BASE;

const ENVS = ["prod", "stg", "dev"];

function nextId(sites) {
  const max = sites.reduce((m, s) => Math.max(m, parseInt(s.id) || 0), 0);
  return String(max + 1);
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(
    () => !!sessionStorage.getItem("admin_authed")
  );
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  const [sites, setSites] = useState([]);
  const [groupOrder, setGroupOrder] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!authed) return;
    fetch(`${API_URL}?action=config`)
      .then((r) => r.json())
      .then((d) => {
        setSites(d.sites || []);
        setGroupOrder(d.group_order || []);
      })
      .catch(() => showToast("Failed to load config", false));
  }, [authed]);

  function showToast(msg, ok) {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  }

  async function handleLogin(e) {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError("");
    try {
      const res = await fetch(`${API_URL}?action=verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        sessionStorage.setItem("admin_authed", "1");
        setAuthed(true);
      } else if (res.status === 401) {
        setAuthError("Wrong password.");
      } else {
        setAuthError("Could not reach the API.");
      }
    } catch {
      setAuthError("Could not reach the API.");
    }
    setAuthLoading(false);
  }

  async function handleSave() {
    setLoading(true);
    try {
      const pwd = sessionStorage.getItem("admin_authed")
        ? password || sessionStorage.getItem("admin_pwd") || ""
        : password;
      const storedPwd = sessionStorage.getItem("admin_pwd") || password;
      const res = await fetch(`${API_URL}?action=config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          password: storedPwd,
          sites,
          group_order: groupOrder,
        }),
      });
      if (res.ok) {
        showToast("Saved!", true);
      } else {
        showToast("Save failed.", false);
      }
    } catch {
      showToast("Network error.", false);
    }
    setLoading(false);
  }

  function addSite() {
    setSites((prev) => [
      ...prev,
      { id: nextId(prev), label: "", group: groupOrder[0] || "", env: "prod", iframe: "", host_ip: "" },
    ]);
  }

  function updateSite(idx, field, value) {
    setSites((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      if (field === "group" && value && !groupOrder.includes(value)) {
        setGroupOrder((go) => [...go, value]);
      }
      return next;
    });
  }

  function deleteSite(idx) {
    setSites((prev) => prev.filter((_, i) => i !== idx));
  }

  function moveGroup(i, dir) {
    const j = i + dir;
    if (j < 0 || j >= groupOrder.length) return;
    setGroupOrder((prev) => {
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  function removeGroup(i) {
    setGroupOrder((prev) => prev.filter((_, idx) => idx !== i));
  }

  const knownGroups = [...new Set([...groupOrder, ...sites.map((s) => s.group).filter(Boolean)])];

  if (!authed) {
    return (
      <div className="admin-login-page">
        <form className="admin-login-card" onSubmit={handleLogin}>
          <h1 className="admin-login-title">Admin</h1>
          <p className="admin-login-sub">webgit.zorem.dev</p>
          {authError && <p className="admin-login-error">{authError}</p>}
          <input
            className="admin-login-input"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              sessionStorage.setItem("admin_pwd", e.target.value);
            }}
            autoFocus
          />
          <button className="admin-login-btn" type="submit" disabled={authLoading}>
            {authLoading ? "Checking…" : "Sign in"}
          </button>
          <a href="#" className="admin-back-link" onClick={(e) => { e.preventDefault(); window.location.hash = ""; }}>
            ← Back to dashboard
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div className="admin-header-inner">
          <h1 className="admin-title">WebGit Admin</h1>
          <div className="admin-header-actions">
            {toast && (
              <span className={`admin-toast ${toast.ok ? "ok" : "err"}`}>{toast.msg}</span>
            )}
            <button className="admin-save-btn" onClick={handleSave} disabled={loading}>
              {loading ? "Saving…" : "Save Changes"}
            </button>
            <a href="#" className="text-btn" onClick={(e) => { e.preventDefault(); window.location.hash = ""; }}>
              ← Dashboard
            </a>
          </div>
        </div>
      </header>

      <main className="admin-main">
        {/* Group order */}
        <section className="admin-section">
          <div className="admin-section-head">
            <h2>Group Order</h2>
          </div>
          <div className="admin-go-list">
            {groupOrder.map((g, i) => (
              <div key={i} className="admin-go-item">
                <span className="admin-go-num">{i + 1}</span>
                <span className="admin-go-name">{g}</span>
                <div className="admin-go-btns">
                  <button onClick={() => moveGroup(i, -1)} disabled={i === 0}>▲</button>
                  <button onClick={() => moveGroup(i, 1)} disabled={i === groupOrder.length - 1}>▼</button>
                  <button className="admin-go-del" onClick={() => removeGroup(i)}>✕</button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Sites table */}
        <section className="admin-section">
          <div className="admin-section-head">
            <h2>
              Sites
              <span className="admin-count">{sites.length}</span>
            </h2>
            <button className="admin-add-btn" onClick={addSite}>+ Add Site</button>
          </div>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Group</th>
                  <th>Env</th>
                  <th>URL (iframe)</th>
                  <th>Host IP</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sites.map((s, i) => (
                  <tr key={s.id}>
                    <td>
                      <input
                        className="admin-input"
                        value={s.label}
                        onChange={(e) => updateSite(i, "label", e.target.value)}
                        placeholder="Label"
                      />
                    </td>
                    <td>
                      <input
                        className="admin-input"
                        value={s.group}
                        onChange={(e) => updateSite(i, "group", e.target.value)}
                        list="admin-groups"
                        placeholder="Group"
                      />
                    </td>
                    <td>
                      <select
                        className="admin-select"
                        value={s.env}
                        onChange={(e) => updateSite(i, "env", e.target.value)}
                      >
                        {ENVS.map((env) => (
                          <option key={env} value={env}>{env.toUpperCase()}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        className="admin-input admin-url"
                        value={s.iframe}
                        onChange={(e) => updateSite(i, "iframe", e.target.value)}
                        placeholder="https://"
                      />
                    </td>
                    <td>
                      <input
                        className="admin-input admin-ip"
                        value={s.host_ip || ""}
                        onChange={(e) => updateSite(i, "host_ip", e.target.value)}
                        placeholder="192.168.x.x"
                      />
                    </td>
                    <td>
                      <button className="admin-del-btn" onClick={() => deleteSite(i)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <datalist id="admin-groups">
              {knownGroups.map((g) => <option key={g} value={g} />)}
            </datalist>
          </div>
        </section>
      </main>
    </div>
  );
}
